import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { UrlAnswerList } from "@/components/url-answer-list";
import { client } from "@/hera-api/client/client.gen";
import type { LicenseResponseSchema } from "@/hera-api/client/types.gen";
import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import { documentAnswerTokens } from "@/lib/answer-file";
import { isNacStyleAnswerType } from "@/lib/nac-answer-types";
import { normalizePhotoAnswers } from "@/lib/photo-answer";
import { trackedFetch } from "@/lib/server-request-activity";
import {
  formatAnswerValue,
  formatConfirmationAnswer,
  formatPhoneNumber,
} from "@/lib/utils";
import { LiveLinkAnswer } from "./live-link-answer";
import { PhotoAnswerReview } from "./photo-answer-review";

interface DeregistrationAnswersCardContentProps {
  questions: QuestionResponseSchema[];
  data: LicenseResponseSchema;
}

export function DeregistrationAnswersCardContent({
  questions,
  data,
}: DeregistrationAnswersCardContentProps) {
  const handleShowDocument = async (token: string) => {
    const baseUrl = client.getConfig().baseUrl || "";
    const auth = client.getConfig().auth;
    const url = `${baseUrl}/shared/answer_files/${token}`;

    try {
      const response = await trackedFetch(url, {
        headers: {
          Authorization: `Bearer ${auth}`,
        },
      });
      if (!response.ok)
        throw new Error(`Failed to load document: ${response.statusText}`);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error("Error loading document:", error);
      window.open(url, "_blank");
    }
  };

  const answers = data.answers_to_additional_questions ?? {};

  const hasAnswer = (answer: unknown): boolean => {
    if (answer === undefined || answer === null) return false;
    if (typeof answer === "string" && answer.trim() === "") return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    return true;
  };

  return (
    <CardContent className="space-y-4">
      {questions
        .filter(
          (q) =>
            q.answer_type !== "information" &&
            !isNacStyleAnswerType(q.answer_type) &&
            hasAnswer(answers[q.id]),
        )
        .map((question) => {
          const answer = answers[question.id];

          if (question.answer_type === "document") {
            const tokens = documentAnswerTokens(answer);
            if (tokens.length === 0) return null;
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {tokens.map((token, index) => (
                    <Button
                      key={token}
                      variant="outline"
                      size="sm"
                      onClick={() => handleShowDocument(token)}
                    >
                      {tokens.length > 1 ? `Show file ${index + 1}` : "Show"}
                    </Button>
                  ))}
                </div>
              </div>
            );
          }

          if (question.answer_type === "url") {
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <UrlAnswerList value={answer} />
              </div>
            );
          }

          if (question.answer_type === "photo") {
            const photos = normalizePhotoAnswers(answer);
            if (photos.length === 0) return null;
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <PhotoAnswerReview photos={photos} property={data.property} />
              </div>
            );
          }

          // Live Link: render a clickable link to the configured URL template.
          if (
            question.answer_type === "live_link" &&
            typeof answer === "string" &&
            answer
          ) {
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <LiveLinkAnswer
                  value={answer}
                  urlTemplate={question.url_template}
                  copyValueToClipboard={
                    question.copy_value_to_clipboard ?? false
                  }
                  httpMethod={question.http_method}
                  valueExtractRegex={question.value_extract_regex}
                  requestFields={question.request_fields}
                  browserHelperConfig={question.browser_helper_config}
                />
              </div>
            );
          }

          if (question.answer_type === "confirmation") {
            return (
              <div key={question.id}>
                <p className="text-sm font-bold">{question.question}</p>
                <p className="text-sm">{formatConfirmationAnswer(answer)}</p>
              </div>
            );
          }

          const formattedAnswer =
            question.answer_type === "phone_number" &&
            typeof answer === "string"
              ? formatPhoneNumber(answer)
              : formatAnswerValue(answer);

          return (
            <div key={question.id}>
              <p className="text-sm font-bold">{question.question}</p>
              <p className="text-sm">{formattedAnswer}</p>
            </div>
          );
        })}
    </CardContent>
  );
}
